import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ChartSuggestion } from '../types';
import { formatColumnName, formatNumber, isMonetaryColumn } from '../utils/formatters';

interface DataChartProps {
  data: any[];
  suggestion: ChartSuggestion;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const DataChart: React.FC<DataChartProps> = ({ data, suggestion }) => {
  if (suggestion.type === 'none' || !suggestion.xAxis) {
    return null;
  }

  // Custom formatter for Y-axis with compact notation
  const formatYAxis = (value: any) => {
    const absValue = Math.abs(value);
    let formattedValue = '';
    
    if (absValue >= 1000000) {
      formattedValue = (value / 1000000).toFixed(1) + 'M';
    } else if (absValue >= 1000) {
      formattedValue = (value / 1000).toFixed(0) + 'K';
    } else {
      formattedValue = value.toFixed(0);
    }
    
    if (typeof suggestion.yAxis === 'string' && isMonetaryColumn(suggestion.yAxis)) {
      return '$' + formattedValue;
    }
    return formattedValue;
  };

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {formatColumnName(entry.dataKey)}: {
                isMonetaryColumn(entry.dataKey) 
                  ? '$' + formatNumber(entry.value, 2)
                  : formatNumber(entry.value, 2)
              }
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Prepare data based on chart type
  const prepareData = () => {
    if (suggestion.type === 'pie' && suggestion.yAxis && typeof suggestion.yAxis === 'string') {
      // For pie charts, we need name and value
      return data.map(item => ({
        name: item[suggestion.xAxis!],
        value: Number(item[suggestion.yAxis as string]) || 0
      }));
    }
    
    if (suggestion.groupBy && suggestion.yAxis && typeof suggestion.yAxis === 'string') {
      // Group data for stacked/grouped charts
      const grouped: Record<string, any> = {};
      const uniqueGroups = new Set<string>();
      
      data.forEach(item => {
        const xKey = item[suggestion.xAxis!];
        const groupKey = item[suggestion.groupBy!];
        
        if (!grouped[xKey]) {
          grouped[xKey] = { [suggestion.xAxis!]: xKey };
        }
        
        // Convert numeric strings to numbers for proper charting
        const value = typeof item[suggestion.yAxis as string] === 'string' 
          ? parseFloat(item[suggestion.yAxis as string]) 
          : Number(item[suggestion.yAxis as string]) || 0;
          
        grouped[xKey][groupKey] = value;
        uniqueGroups.add(groupKey);
      });
      
      // Ensure all groups have all keys (fill missing with 0)
      const allGroups = Array.from(uniqueGroups);
      Object.values(grouped).forEach(row => {
        allGroups.forEach(group => {
          if (!(group in row)) {
            row[group] = 0;
          }
        });
      });
      
      return Object.values(grouped);
    }
    
    // For multi-line charts with array of yAxis
    if (Array.isArray(suggestion.yAxis)) {
      return data.map(item => {
        const point: any = { [suggestion.xAxis!]: item[suggestion.xAxis!] };
        (suggestion.yAxis as string[]).forEach((y: string) => {
          point[y] = Number(item[y]) || 0;
        });
        return point;
      });
    }
    
    // Standard data preparation
    return data.map(item => {
      const value = typeof item[suggestion.yAxis as string] === 'string'
        ? parseFloat(item[suggestion.yAxis as string])
        : Number(item[suggestion.yAxis as string]) || 0;
        
      return {
        [suggestion.xAxis!]: item[suggestion.xAxis!],
        [suggestion.yAxis as string]: value,
        ...item // Include all original data for tooltip
      };
    });
  };

  const chartData = prepareData();

  // Calculate Y-axis domain to ensure proper scaling
  const getYAxisDomain = () => {
    let minValue = 0;
    let maxValue = 0;

    if (suggestion.type === 'pie') {
      return undefined; // Pie charts don't need Y-axis domain
    }

    if (suggestion.groupBy) {
      // For grouped/stacked charts, find max across all groups
      chartData.forEach(item => {
        const values = Object.keys(item)
          .filter(key => key !== suggestion.xAxis)
          .map(key => Number(item[key]) || 0);
        const sum = suggestion.type === 'stacked-bar' 
          ? values.reduce((a, b) => a + b, 0) // Sum for stacked
          : Math.max(...values); // Max for grouped
        maxValue = Math.max(maxValue, sum);
      });
    } else if (Array.isArray(suggestion.yAxis)) {
      // For multi-line charts
      chartData.forEach(item => {
        (suggestion.yAxis as string[]).forEach(y => {
          const value = Number(item[y]) || 0;
          maxValue = Math.max(maxValue, value);
          minValue = Math.min(minValue, value);
        });
      });
    } else if (typeof suggestion.yAxis === 'string') {
      // For single series charts
      chartData.forEach(item => {
        const value = Number(item[suggestion.yAxis as string]) || 0;
        maxValue = Math.max(maxValue, value);
        minValue = Math.min(minValue, value);
      });
    }

    // Add 10% padding to the max value for better visualization
    const padding = (maxValue - minValue) * 0.1;
    return [Math.min(0, minValue - padding), maxValue + padding];
  };

  const yAxisDomain = getYAxisDomain();

  // Get unique values for legend in grouped/stacked charts
  const getUniqueGroups = () => {
    if (!suggestion.groupBy) return [];
    const groups = new Set<string>();
    data.forEach(item => groups.add(item[suggestion.groupBy!]));
    return Array.from(groups);
  };

  const renderChart = () => {
    switch (suggestion.type) {
      case 'bar':
        return (
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={suggestion.xAxis} />
            <YAxis tickFormatter={formatYAxis} domain={yAxisDomain} width={80} />
            <Tooltip content={<CustomTooltip />} />
            <Legend formatter={(value) => formatColumnName(value)} />
            <Bar dataKey={suggestion.yAxis as string} fill={COLORS[0]} />
          </BarChart>
        );

      case 'grouped-bar':
        const groups = getUniqueGroups();
        return (
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={suggestion.xAxis} />
            <YAxis tickFormatter={formatYAxis} domain={yAxisDomain} width={80} />
            <Tooltip content={<CustomTooltip />} />
            <Legend formatter={(value) => formatColumnName(value)} />
            {groups.map((group, index) => (
              <Bar key={group} dataKey={group} fill={COLORS[index % COLORS.length]} />
            ))}
          </BarChart>
        );

      case 'stacked-bar':
        const stackGroups = getUniqueGroups();
        return (
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={suggestion.xAxis} />
            <YAxis tickFormatter={formatYAxis} domain={yAxisDomain} width={80} />
            <Tooltip content={<CustomTooltip />} />
            <Legend formatter={(value) => formatColumnName(value)} />
            {stackGroups.map((group, index) => (
              <Bar key={group} dataKey={group} stackId="a" fill={COLORS[index % COLORS.length]} />
            ))}
          </BarChart>
        );

      case 'line':
        return (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={suggestion.xAxis} />
            <YAxis tickFormatter={formatYAxis} domain={yAxisDomain} width={80} />
            <Tooltip content={<CustomTooltip />} />
            <Legend formatter={(value) => formatColumnName(value)} />
            <Line 
              type="monotone" 
              dataKey={suggestion.yAxis as string} 
              stroke={COLORS[0]} 
              strokeWidth={2}
            />
          </LineChart>
        );

      case 'multi-line':
        return (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={suggestion.xAxis} />
            <YAxis tickFormatter={formatYAxis} domain={yAxisDomain} width={80} />
            <Tooltip content={<CustomTooltip />} />
            <Legend formatter={(value) => formatColumnName(value)} />
            {Array.isArray(suggestion.yAxis) && suggestion.yAxis.map((y, index) => (
              <Line 
                key={y}
                type="monotone" 
                dataKey={y} 
                stroke={COLORS[index % COLORS.length]} 
                strokeWidth={2}
              />
            ))}
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={suggestion.xAxis} />
            <YAxis tickFormatter={formatYAxis} domain={yAxisDomain} width={80} />
            <Tooltip content={<CustomTooltip />} />
            <Legend formatter={(value) => formatColumnName(value)} />
            <Area 
              type="monotone" 
              dataKey={suggestion.yAxis as string} 
              stroke={COLORS[0]} 
              fill={COLORS[0]} 
              fillOpacity={0.6}
            />
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: any) => {
                if (typeof suggestion.yAxis === 'string' && isMonetaryColumn(suggestion.yAxis)) {
                  return '$' + formatNumber(value, 2);
                }
                return formatNumber(value, 2);
              }}
            />
          </PieChart>
        );

      case 'scatter':
        return (
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey={suggestion.xAxis} 
              type="number"
              tickFormatter={(value) => formatNumber(value, 0)}
            />
            <YAxis 
              dataKey={suggestion.yAxis as string} 
              type="number"
              tickFormatter={formatYAxis}
              domain={yAxisDomain}
              width={80}
            />
            <Tooltip 
              cursor={{ strokeDasharray: '3 3' }}
              content={<CustomTooltip />}
            />
            <Scatter data={chartData} fill={COLORS[0]} />
          </ScatterChart>
        );

      default:
        return <div>Unsupported chart type</div>;
    }
  };

  return (
    <div className="w-full">
      {suggestion.title && (
        <h4 className="text-sm font-medium mb-2">{suggestion.title}</h4>
      )}
      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
      {suggestion.rationale && (
        <p className="text-xs text-muted-foreground mt-2">{suggestion.rationale}</p>
      )}
    </div>
  );
};

export default DataChart;